/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { Components, DateDelta, TimeDelta } from '../types';
import { compareNumbers, compareBigInts } from './core';
import { EPOCH_DAYS, NANOS_OF_DAY, NANOS_OF_EPOCH } from './types';
import { getDatePart, DatePart, MONTHS_PER_YEAR, getDaysInMonth, getDate } from './date';
import { NANOSECONDS_PER_DAY, NANOSECONDS_PER_MILLISECOND, getTime } from './time';
import { getTimeOfInstant, getInstant } from './instant';

// Duration utilities

/* @internal */ export function dateTimeDiff(leftDate: EPOCH_DAYS, leftTime: NANOS_OF_DAY, rightDate: EPOCH_DAYS, rightTime: NANOS_OF_DAY) {
    return diff(leftDate, leftTime, rightDate, rightTime);
}

/* @internal */ export function dateDiff(leftDate: EPOCH_DAYS, rightDate: EPOCH_DAYS) {
    return diff(leftDate, undefined, rightDate, undefined);
}

/* @internal */ export function timeDiff(leftTime: NANOS_OF_DAY, rightTime: NANOS_OF_DAY) {
    return diff(undefined, leftTime, undefined, rightTime);
}

/* @internal */ export function instantDiff(leftTicks: NANOS_OF_EPOCH, rightTicks: NANOS_OF_EPOCH) {
    return diff(undefined, leftTicks, undefined, rightTicks);
}

function diff(leftDate: EPOCH_DAYS | undefined, leftTime: bigint | undefined, rightDate: EPOCH_DAYS | undefined, rightTime: bigint | undefined) {
    const deltas: Components<DateDelta | TimeDelta> = {};

    let sign = 1;
    let startDate = leftDate;
    let startTime = leftTime;
    let endDate = rightDate;
    let endTime = rightTime;

    const cmpDate = endDate !== undefined && startDate !== undefined ? compareNumbers(endDate, startDate) : 0;
    const cmpTime = endTime !== undefined && startTime !== undefined ? compareBigInts(endTime, startTime) : 0;
    if ((cmpDate || cmpTime) < 0) {
        sign = -1;
        startDate = rightDate;
        startTime = rightTime;
        endDate = leftDate;
        endTime = leftTime;
    }

    if (startDate !== undefined && endDate !== undefined) {
        const { year: startYear, month: startMonth, day: startDay } = getDatePart(startDate, DatePart.all);
        let { year: endYear, month: endMonth, day: endDay } = getDatePart(endDate, DatePart.all);
        if (endTime !== undefined && startTime !== undefined && endTime < startTime) {
            endDay--;
            if (endDay < 1) {
                endMonth--;
                if (endMonth < 1) {
                    endYear--;
                    endMonth += MONTHS_PER_YEAR;
                }
                endDay += getDaysInMonth(endYear, endMonth);
            }
            endTime = (endTime + NANOSECONDS_PER_DAY) as NANOS_OF_DAY;
        }

        let years = 0;
        let months = 0;
        let days = 0;
        if (endDay >= startDay) {
            days = endDay - startDay;
            months = endMonth - startMonth;
            years = endYear - startYear;
        }
        else {
            days = endDay - startDay + getDaysInMonth(endYear, endMonth - 1);
            months = endMonth - startMonth - 1;
            years = endYear - startYear;
        }

        years += Math.floor(months / MONTHS_PER_YEAR);
        months = months % MONTHS_PER_YEAR;
        if (months < 0) months += MONTHS_PER_YEAR;

        years *= sign;
        months *= sign;
        days *= sign;

        if (years !== 0) deltas.years = years;
        if (months !== 0) deltas.months = months;
        if (days !== 0) deltas.days = days;
    }

    if (startTime !== undefined && endTime !== undefined) {
        const time = (endTime - startTime) * BigInt(sign);
        const milliseconds = Number(time / NANOSECONDS_PER_MILLISECOND);
        const nanoseconds = Number(time % NANOSECONDS_PER_MILLISECOND);
        if (milliseconds !== 0) deltas.milliseconds = milliseconds;
        if (nanoseconds !== 0) deltas.nanoseconds = nanoseconds;
    }

    return deltas;
}

/* @internal */ export function dateTimePlus(date: EPOCH_DAYS, time: NANOS_OF_DAY, years: number, months: number, days: number, hours: number, minutes: number, seconds: number, milliseconds: number, nanoseconds: number) {
    return getInstant(nominalDurationPlus(date, years, months, days), accurateDurationPlus(time, hours, minutes, seconds, milliseconds, nanoseconds));
}

/* @internal */ export function datePlus(date: EPOCH_DAYS, years: number, months: number, days: number) {
    return nominalDurationPlus(date, years, months, days);
}

/* @internal */ export function timePlus(time: NANOS_OF_DAY, hours: number, minutes: number, seconds: number, milliseconds: number, nanoseconds: number) {
    return getTimeOfInstant(accurateDurationPlus(time, hours, minutes, seconds, milliseconds, nanoseconds));
}

/* @internal */ export function instantPlus(time: NANOS_OF_EPOCH, hours: number, minutes: number, seconds: number, milliseconds: number, nanoseconds: number) {
    return accurateDurationPlus(time, hours, minutes, seconds, milliseconds, nanoseconds);
}

function nominalDurationPlus(date: EPOCH_DAYS, years: number, months: number, days: number) {
    if (years !== 0) {
        const { year, month, day } = getDatePart(date, DatePart.all);
        date = getDate(year + years, month, day);
    }
    if (months !== 0) {
        const { year, month, day } = getDatePart(date, DatePart.all);
        date = getDate(year, month + months, day);
    }
    if (days !== 0) {
        const { year, month, day } = getDatePart(date, DatePart.all);
        date = getDate(year, month, day + days);
    }
    return date;
}

function accurateDurationPlus(time: bigint, hours: number, minutes: number, seconds: number, milliseconds: number, nanoseconds: number) {
    return (time + getTime(hours, minutes, seconds, milliseconds, nanoseconds)) as NANOS_OF_EPOCH;
}